import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import Button from '@mui/material/Button';
import styled from '@mui/material/styles/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { INSTANCE_TYPES } from '../../../constants';
import {
  MONITOR_PERIODS,
  REFRESH_RATES,
} from '../../../constants/monitoringConstants';
import { useUser } from '../../../contexts/UserContext';
import Text from '../../elements/text/Text';

const StyledResponsiveContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '1em',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down(1050)]: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

/**
 * Monitoring Settings component. Basically it's a form to configure the monitoring charts
 * @param instance - instance info
 * @param settings - settings for monitoring chart setted by user. This values are controlled by parent component
 * @param onNodeChange - function to handle node change
 * @param onTermChange - function to handle input term changes
 * @param onDateTimeChange - function to handle input date time changes
 * @param onRefreshChange - function to handle input refresh rate changes
 * @returns {JSX.Element}
 */
const MonitoringSettings = ({
  instance,
  settings,
  onNodeChange,
  onTermChange,
  onDateTimeChange,
  onRefreshChange,
}) => {
  const { t } = useTranslation('monitoring');
  const { user } = useUser();

  const isSolrCloud = instance.type === INSTANCE_TYPES.SOLRCLOUD;

  return (
    <section>
      {isSolrCloud && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
          <Text bodyBold>{t('instanceNode')}</Text>
          <FormControl
            size="small"
            style={{ minWidth: 250 }}
            variant="outlined"
          >
            <Select
              onChange={(e) => onNodeChange(e.target.value)}
              value={settings.node}
              data-testid={'node-select'}
            >
              {instance.instances.map((node) => (
                <MenuItem value={node.subscriptNumber} key={node.id}>
                  {`${instance.name} - ${node.subscriptNumber}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <StyledResponsiveContainer>
        <Box sx={{ display: 'flex', gap: '1em' }}>
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 150 }}
          >
            <InputLabel>{t('timePeriod')}</InputLabel>
            <Select
              label={t('timePeriod')}
              onChange={(e) => onTermChange(e.target.value)}
              defaultValue={900}
              data-testid={'term-select'}
            >
              {MONITOR_PERIODS.map((period) => (
                <MenuItem value={period.value} key={period.value}>
                  {t(period.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={t('endDateTime')}
            type="datetime-local"
            variant="outlined"
            size="small"
            disabled={settings.refresh > 0}
            value={moment(settings.endDateTime)
              .tz(user?.timeZone)
              .format('YYYY-MM-DDTHH:mm:ss')}
            onChange={(e) => onDateTimeChange(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              max: moment()
                .add(1, 's')
                .tz(user?.timeZone)
                .format('YYYY-MM-DDTHH:mm:ss'),
            }}
            data-testid="datetime"
          />

          <Button
            variant="outlined"
            disabled={settings.refresh > 0}
            onClick={() =>
              onDateTimeChange(moment().format('YYYY-MM-DDTHH:mm:ss'))
            }
          >
            {t('now')}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: '0.5em' }}>
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 150 }}
          >
            <InputLabel>{t('refreshRate', { ns: 'monitoring' })}</InputLabel>
            <Select
              label={t('refreshRate', { ns: 'monitoring' })}
              onChange={(e) => onRefreshChange(e.target.value)}
              defaultValue={0}
              data-testid={'datetime-select'}
            >
              {REFRESH_RATES.map((period) => (
                <MenuItem value={period.value} key={period.value}>
                  {t(period.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </StyledResponsiveContainer>
    </section>
  );
};

MonitoringSettings.propTypes = {
  instance: PropTypes.object,
  settings: PropTypes.object.isRequired,
  onNodeChange: PropTypes.func,
  onTermChange: PropTypes.func.isRequired,
  onDateTimeChange: PropTypes.func.isRequired,
  onRefreshChange: PropTypes.func.isRequired,
};

export default MonitoringSettings;
